import {
  ChannelIcon,
  DateField,
  Dialog,
  Empty,
  ErrorState,
  Loading,
  PageHead,
  Select,
  useFormat,
} from '@ly360/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Megaphone, MessageSquare, Newspaper, Pause, Play, Plus, Send } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useLocation, useNavigate, useParams } from 'react-router';
import { Field } from '../crm/forms';
import { api } from '../lib/api';
import { useAuth } from '../lib/auth';
import { iconFor } from '../integrations/Connect';
import { PostStatus, type Post } from '../publishing/Publishing';
import { NewCampaignDialog, type CampaignPreset } from './CampaignForm';
import { RETAIL_KEYS, roas, type Campaign, type CampaignResults } from './data';
import { BarList, DailyChart, DialogButtons, PlatformLabel, SourceLabel } from './ui';

const STATUS_TONE: Record<string, string> = {
  draft: 'badge-neutral',
  scheduled: 'badge',
  sending: 'badge',
  running: 'badge-success',
  paused: 'badge-warning',
  completed: 'badge-neutral',
  cancelled: 'badge-neutral',
};

function CampaignStatus({ value }: { value: string }) {
  const { t } = useTranslation();
  return (
    <span className={`badge ${STATUS_TONE[value] ?? 'badge-neutral'}`}>
      {t(`retail.campaignStatus.${value}`)}
    </span>
  );
}

/** Where a campaign goes: posts, message channels and ad platforms, as small marks. */
function Parts({ c }: { c: Campaign }) {
  const { t } = useTranslation();
  return (
    <span className="campaign-parts">
      {c.posts > 0 && (
        <span title={t('retail.campaign.parts.post')} className="part-mark">
          <Newspaper size={13} />
        </span>
      )}
      {c.channels.map((ch) => (
        <span key={ch} title={t(`channels.${ch}`)}>
          <ChannelIcon channel={ch} size={11} />
        </span>
      ))}
      {c.ads.map((a) => (
        <span key={a.platform} title={t(`retail.platform.${a.platform}`)}>
          <ChannelIcon channel={a.platform} size={11} />
        </span>
      ))}
    </span>
  );
}

const ratio = (x: number | null, fmtN: (n: number) => string) =>
  x === null ? '—' : `${fmtN(Math.round(x * 10) / 10)}×`;

export function CampaignsPage() {
  const { t } = useTranslation();
  const fmt = useFormat();
  const { can, session } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  // Opened from the command bar ("follow up with everyone who asked about …"): a campaign ready to check.
  const location = useLocation();
  const [asked, setAsked] = useState<CampaignPreset | null>(
    () => (location.state as { preset?: CampaignPreset } | null)?.preset ?? null,
  );
  useEffect(() => {
    if ((location.state as { preset?: unknown } | null)?.preset)
      void navigate(location.pathname, { replace: true, state: null });
  }, [location, navigate]);
  const list = useQuery({
    queryKey: ['campaigns', 'list'],
    queryFn: () => api<{ items: Campaign[] }>('/v1/campaigns'),
  });
  const currency = session!.tenant.currency;
  const money = (minor: number) => fmt.money(minor, currency);
  const all = list.data?.items ?? [];
  const sum = (k: keyof CampaignResults) => all.reduce((s, c) => s + (c.results?.[k] ?? 0), 0);
  const adSpend = sum('spendMinor');
  const adRevenue = all.filter((c) => c.ads.length).reduce((s, c) => s + (c.results?.revenueMinor ?? 0), 0);

  return (
    <>
      <PageHead
        eyebrow={t('nav.marketing')}
        title={t('retail.campaigns.title')}
        subtitle={t('retail.campaigns.subtitle')}
        actions={
          can('campaigns:manage') && (
            <button className="btn btn-grad" onClick={() => setOpen(true)} data-testid="add-campaign">
              <Plus size={16} />
              {t('retail.campaign.new')}
            </button>
          )
        }
      />
      <div className="stat-grid" style={{ marginBottom: '1rem' }} data-testid="campaign-stats">
        <div className="stat">
          <div className="label">{t('retail.campaigns.revenue')}</div>
          <div className="value">{money(sum('revenueMinor'))}</div>
          <div className="stat-note faint">{t('retail.growth.ordersCount', { count: sum('orders') })}</div>
        </div>
        <div className="stat">
          <div className="label">{t('retail.campaigns.adSpend')}</div>
          <div className="value">{money(adSpend)}</div>
          <div className="stat-note faint">
            {t('retail.campaigns.adReturn', { value: ratio(roas(adRevenue, adSpend), fmt.number) })}
          </div>
        </div>
        <div className="stat">
          <div className="label">{t('retail.campaigns.chats')}</div>
          <div className="value">{fmt.number(sum('conversations'))}</div>
          <div className="stat-note faint">{t('retail.campaigns.chatsHint')}</div>
        </div>
        <div className="stat">
          <div className="label">{t('retail.campaigns.messagesSent')}</div>
          <div className="value">{fmt.number(sum('sent'))}</div>
          <div className="stat-note faint">{t('retail.campaigns.replies', { count: sum('replied') })}</div>
        </div>
        <div className="stat">
          <div className="label">{t('retail.campaigns.postsPublished')}</div>
          <div className="value">{fmt.number(sum('postsPublished'))}</div>
          <div className="stat-note faint">
            <Link to="/publishing">{t('nav.publishing')}</Link>
          </div>
        </div>
      </div>
      <section className="card">
        {list.isLoading && <Loading />}
        {list.isError && <ErrorState error={list.error} />}
        {list.data && !all.length && <Empty text={t('retail.campaigns.empty')} />}
        {!!all.length && (
          <div className="table-wrap">
            <table className="table table-responsive" data-testid="campaigns-table">
              <thead>
                <tr>
                  <th>{t('retail.col.campaign')}</th>
                  <th>{t('retail.col.where')}</th>
                  <th>{t('retail.col.status')}</th>
                  <th className="num">{t('retail.col.reached')}</th>
                  <th className="num">{t('retail.col.chats')}</th>
                  <th className="num">{t('retail.col.orders')}</th>
                  <th className="num">{t('retail.col.revenue')}</th>
                  <th className="num">{t('retail.col.spend')}</th>
                  <th className="num">{t('retail.col.roas')}</th>
                </tr>
              </thead>
              <tbody>
                {all.map((c) => {
                  const r = c.results!;
                  const x = roas(r.revenueMinor, r.spendMinor);
                  return (
                    <tr
                      key={c.id}
                      onClick={() => navigate(`/campaigns/${c.id}`)}
                      style={{ cursor: 'pointer' }}
                    >
                      <td data-label={t('retail.col.campaign')}>
                        <Link to={`/campaigns/${c.id}`} onClick={(e) => e.stopPropagation()} dir="auto">
                          <strong>{c.name}</strong>
                        </Link>
                        {c.offerCode && (
                          <span className="faint mono" style={{ display: 'block', fontSize: 'var(--t-xs)' }}>
                            {c.offerCode}
                          </span>
                        )}
                      </td>
                      <td data-label={t('retail.col.where')}>
                        <Parts c={c} />
                      </td>
                      <td data-label={t('retail.col.status')}>
                        <CampaignStatus value={c.status} />
                      </td>
                      <td data-label={t('retail.col.reached')} className="num">
                        {fmt.number(r.sent + r.impressions)}
                      </td>
                      <td data-label={t('retail.col.chats')} className="num">
                        {fmt.number(r.conversations + r.replied)}
                      </td>
                      <td data-label={t('retail.col.orders')} className="num">
                        {fmt.number(r.orders)}
                      </td>
                      <td data-label={t('retail.col.revenue')} className="num">
                        {money(r.revenueMinor)}
                      </td>
                      <td data-label={t('retail.col.spend')} className="num">
                        {r.spendMinor ? money(r.spendMinor) : '—'}
                      </td>
                      <td
                        data-label={t('retail.col.roas')}
                        className={`num ${x !== null && x < 1 ? 'trend-down' : x !== null ? 'trend-up' : ''}`}
                      >
                        {ratio(x, fmt.number)}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </section>
      {open && <NewCampaignDialog open onClose={() => setOpen(false)} />}
      {asked && <NewCampaignDialog open preset={asked} onClose={() => setAsked(null)} />}
    </>
  );
}

interface PlatformRow {
  platform: string;
  adIds: string[];
  budgetMinor: string | null;
  spendMinor: string;
  impressions: string;
  clicks: string;
  leads: string;
  chats: number;
  revenueMinor: string;
}
interface CampaignDetail {
  campaign: Campaign;
  results: CampaignResults;
  daily: { day: string; spendMinor: string; revenueMinor: string }[];
  byPlatform: PlatformRow[];
  byChannel: { channel: string; sent: number; skipped: number }[];
  orders: {
    id: string;
    number: string;
    totalMinor: string;
    currency: string;
    source: string;
    placedAt: string;
    attributionReason: string;
    personId: string | null;
    personName: string | null;
  }[];
  skipped: { reason: string; n: number }[];
}

function Stat({
  label,
  value,
  note,
  tone,
}: {
  label: string;
  value: string;
  note?: string;
  tone?: 'up' | 'down';
}) {
  return (
    <div className="stat">
      <div className="label">{label}</div>
      <div className={`value${tone === 'down' ? ' trend-down' : tone === 'up' ? ' trend-up' : ''}`}>
        {value}
      </div>
      {note && <div className="stat-note faint">{note}</div>}
    </div>
  );
}

export function CampaignPage() {
  const { id } = useParams();
  const { t } = useTranslation();
  const fmt = useFormat();
  const { can } = useAuth();
  const qc = useQueryClient();
  const [confirm, setConfirm] = useState(false);
  const [metrics, setMetrics] = useState(false);
  const q = useQuery({
    queryKey: ['campaign', id],
    queryFn: () => api<CampaignDetail>(`/v1/campaigns/${id}`),
  });
  const posts = useQuery({
    queryKey: ['posts', 'campaign', id],
    queryFn: () => api<{ items: Post[] }>(`/v1/posts?campaignId=${id}`),
  });
  const refresh = () => {
    for (const k of [...RETAIL_KEYS, 'posts']) void qc.invalidateQueries({ queryKey: [k] });
  };
  const send = useMutation({
    mutationFn: () =>
      api<{ sent: number; skipped: number }>(`/v1/campaigns/${id}/send`, { method: 'POST', body: '{}' }),
    onSuccess: () => {
      setConfirm(false);
      refresh();
    },
  });
  const status = useMutation({
    mutationFn: (s: string) =>
      api(`/v1/campaigns/${id}`, { method: 'PATCH', body: JSON.stringify({ status: s }) }),
    onSuccess: refresh,
  });
  if (q.isLoading) return <Loading />;
  if (q.isError || !q.data) return <ErrorState error={q.error} onRetry={() => void q.refetch()} />;
  const { campaign: c, results: r } = q.data;
  const money = (minor: number | string) => fmt.money(Number(minor), c.currency);
  const x = roas(r.revenueMinor, r.spendMinor);
  const manage = can('campaigns:manage');
  const canSend = c.channels.length > 0 && !c.sentAt && manage;

  return (
    <>
      <PageHead
        eyebrow={t('retail.campaigns.title')}
        title={c.name}
        subtitle={[
          t(`retail.objective.${c.objective}`),
          c.occasion ? t(`retail.occasion.${c.occasion}`) : null,
          c.offerCode,
        ]
          .filter(Boolean)
          .join(' · ')}
        actions={
          <>
            <Parts c={c} />
            <CampaignStatus value={c.status} />
            {canSend && (
              <button className="btn btn-grad" onClick={() => setConfirm(true)} data-testid="send-campaign">
                <Send size={16} />
                {t('retail.campaign.sendNow')}
              </button>
            )}
            {c.ads.length > 0 && manage && (
              <button className="btn" onClick={() => setMetrics(true)} data-testid="add-results">
                {t('retail.campaign.addResults')}
              </button>
            )}
            {manage && c.status === 'running' && (
              <button className="btn btn-ghost" onClick={() => status.mutate('paused')}>
                <Pause size={15} />
                {t('retail.campaign.pause')}
              </button>
            )}
            {manage && ['draft', 'paused'].includes(c.status) && c.ads.length > 0 && (
              <button className="btn btn-ghost" onClick={() => status.mutate('running')}>
                <Play size={15} />
                {t('retail.campaign.resume')}
              </button>
            )}
          </>
        }
      />
      <div className="stat-grid" style={{ marginBottom: '1rem' }} data-testid="campaign-results">
        <Stat
          label={t('retail.result.revenue')}
          value={money(r.revenueMinor)}
          note={t('retail.growth.ordersCount', { count: r.orders })}
        />
        {c.ads.length > 0 && (
          <Stat
            label={t('retail.result.roas')}
            value={ratio(x, fmt.number)}
            note={t('retail.result.spentOf', { spend: money(r.spendMinor) })}
            tone={x !== null && x < 1 ? 'down' : 'up'}
          />
        )}
        {c.channels.length > 0 && (
          <Stat
            label={t('retail.result.sent')}
            value={fmt.number(r.sent)}
            note={t('retail.result.repliedSkipped', { replied: r.replied, skipped: r.skipped })}
          />
        )}
        {c.ads.length > 0 && (
          <Stat
            label={t('retail.result.chats')}
            value={fmt.number(r.conversations + r.leads)}
            note={t('retail.result.chatsHint')}
          />
        )}
        {c.posts > 0 && <Stat label={t('retail.result.posts')} value={fmt.number(r.postsPublished)} />}
      </div>

      <div className="record-grid">
        <div style={{ display: 'grid', gap: '1rem', minWidth: 0 }}>
          {c.ads.length > 0 && (
            <section className="card" data-testid="ads-by-platform">
              <div className="card-head">
                <h2>
                  <Megaphone size={16} /> {t('retail.campaign.parts.ads')}
                </h2>
              </div>
              <div className="table-wrap">
                <table className="table table-responsive">
                  <thead>
                    <tr>
                      <th>{t('retail.col.platform')}</th>
                      <th className="num">{t('retail.col.spend')}</th>
                      <th className="num">{t('retail.result.impressions')}</th>
                      <th className="num">{t('retail.col.chats')}</th>
                      <th className="num">{t('retail.col.revenue')}</th>
                      <th className="num">{t('retail.col.roas')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {q.data.byPlatform.map((p) => {
                      const rr = roas(Number(p.revenueMinor), Number(p.spendMinor));
                      return (
                        <tr key={p.platform}>
                          <td data-label={t('retail.col.platform')}>
                            <PlatformLabel platform={p.platform} short />
                            {p.adIds.length > 0 && (
                              <span
                                className="faint mono"
                                style={{ display: 'block', fontSize: 'var(--t-xs)' }}
                              >
                                {p.adIds.join(', ')}
                              </span>
                            )}
                          </td>
                          <td data-label={t('retail.col.spend')} className="num">
                            {money(p.spendMinor)}
                          </td>
                          <td data-label={t('retail.result.impressions')} className="num">
                            {fmt.number(Number(p.impressions))}
                          </td>
                          <td data-label={t('retail.col.chats')} className="num">
                            {fmt.number(p.chats + Number(p.leads))}
                          </td>
                          <td data-label={t('retail.col.revenue')} className="num">
                            {money(p.revenueMinor)}
                          </td>
                          <td
                            data-label={t('retail.col.roas')}
                            className={`num ${rr !== null && rr < 1 ? 'trend-down' : rr !== null ? 'trend-up' : ''}`}
                          >
                            {ratio(rr, fmt.number)}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              {q.data.daily.length > 0 && (
                <div className="card-pad">
                  <DailyChart
                    series={q.data.daily.map((d) => ({
                      day: d.day,
                      revenueMinor: d.revenueMinor,
                      orders: 0,
                    }))}
                    currency={c.currency}
                    label={t('retail.campaign.dailyRevenue')}
                  />
                </div>
              )}
            </section>
          )}
          {c.posts > 0 && (
            <section className="card" data-testid="campaign-posts">
              <div className="card-head">
                <h2>
                  <Newspaper size={16} /> {t('retail.campaign.parts.post')}
                </h2>
                <Link to="/publishing" className="btn btn-ghost btn-sm">
                  {t('nav.publishing')}
                </Link>
              </div>
              <ul className="list-plain post-list">
                {(posts.data?.items ?? []).map((p) => (
                  <li key={p.id} className="post-row">
                    <span className="grow ellipsis" dir="auto">
                      {p.body}
                    </span>
                    <span className="cal-icons">
                      {(p.targets ?? []).map((tg) => (
                        <span
                          key={tg.id}
                          className={`target-dot is-${tg.status}`}
                          title={`${tg.accountName}: ${t(`publishing.status.${tg.status}`)}`}
                        >
                          <ChannelIcon channel={iconFor(tg.provider)} size={11} />
                        </span>
                      ))}
                    </span>
                    <PostStatus value={p.status} />
                  </li>
                ))}
              </ul>
            </section>
          )}
          {c.channels.length > 0 && (
            <section className="card card-pad" data-testid="campaign-messages">
              <h2 className="section-title">
                <MessageSquare size={16} /> {t('retail.campaign.parts.messages')}
              </h2>
              {r.recipients > 0 ? (
                <>
                  <BarList
                    testId="campaign-funnel"
                    rows={[
                      {
                        key: 'aud',
                        label: t('retail.result.audience'),
                        value: r.recipients,
                        display: fmt.number(r.recipients),
                      },
                      ...q.data.byChannel
                        .filter((b) => b.channel !== 'none')
                        .map((b) => ({
                          key: b.channel,
                          label: (
                            <span className="channel-badge">
                              <ChannelIcon channel={b.channel} size={11} /> {t(`channels.${b.channel}`)}
                            </span>
                          ),
                          value: b.sent,
                          display: fmt.number(b.sent),
                        })),
                      {
                        key: 'rep',
                        label: t('retail.result.replied'),
                        value: r.replied,
                        display: fmt.number(r.replied),
                      },
                      {
                        key: 'ord',
                        label: t('retail.result.orders'),
                        value: r.orders,
                        display: fmt.number(r.orders),
                      },
                    ]}
                  />
                  {q.data.skipped.length > 0 && (
                    <p className="faint" style={{ fontSize: 'var(--t-xs)' }}>
                      {q.data.skipped.map((s) => t(`retail.skip.${s.reason}`, { count: s.n })).join(' · ')}
                    </p>
                  )}
                </>
              ) : (
                <p className="faint" style={{ margin: 0 }}>
                  {t('retail.campaign.notSentYet', {
                    channels: c.channels.map((ch) => t(`channels.${ch}`)).join(' → '),
                  })}
                </p>
              )}
            </section>
          )}
          <section className="card">
            <div className="card-head">
              <h2>{t('retail.campaign.creditedOrders')}</h2>
            </div>
            <p className="faint card-pad" style={{ margin: 0, paddingBottom: 0 }}>
              {t('retail.campaign.creditHint', { days: c.attributionDays })}
            </p>
            {q.data.orders.length ? (
              <div className="table-wrap">
                <table className="table table-responsive" data-testid="credited-orders">
                  <thead>
                    <tr>
                      <th>{t('retail.col.order')}</th>
                      <th>{t('retail.col.customer')}</th>
                      <th>{t('retail.col.where')}</th>
                      <th>{t('retail.col.why')}</th>
                      <th className="num">{t('retail.col.total')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {q.data.orders.map((o) => (
                      <tr key={o.id}>
                        <td data-label={t('retail.col.order')}>
                          <span className="mono">{o.number}</span>
                          <span className="faint" style={{ display: 'block', fontSize: 'var(--t-xs)' }}>
                            {fmt.dateTime(o.placedAt)}
                          </span>
                        </td>
                        <td data-label={t('retail.col.customer')}>
                          {o.personId ? (
                            <Link to={`/people/${o.personId}`} dir="auto">
                              {o.personName}
                            </Link>
                          ) : (
                            '—'
                          )}
                        </td>
                        <td data-label={t('retail.col.where')}>
                          <SourceLabel source={o.source} />
                        </td>
                        <td data-label={t('retail.col.why')}>
                          <span className="badge badge-neutral">
                            {t(`retail.reason.${o.attributionReason}`)}
                          </span>
                        </td>
                        <td data-label={t('retail.col.total')} className="num">
                          {money(o.totalMinor)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <Empty text={t('retail.campaign.noOrders')} />
            )}
          </section>
        </div>
        <aside className="side-stack">
          <section className="card card-pad">
            <h2 className="section-title">{t('retail.campaign.details')}</h2>
            <dl className="kv">
              <dt>{t('retail.campaign.audience')}</dt>
              <dd dir="auto">
                {c.segmentId ? <Link to={`/segments?open=${c.segmentId}`}>{c.segmentName}</Link> : '—'}
              </dd>
              {c.templateName && (
                <>
                  <dt>{t('retail.campaign.template')}</dt>
                  <dd className="mono">{c.templateName}</dd>
                </>
              )}
              {c.offerCode && (
                <>
                  <dt>{t('retail.campaign.offerCode')}</dt>
                  <dd className="mono">{c.offerCode}</dd>
                </>
              )}
              {c.startsAt && (
                <>
                  <dt>{t('retail.campaign.startsAt')}</dt>
                  <dd>{fmt.dateTime(c.startsAt)}</dd>
                </>
              )}
              {c.sentAt && (
                <>
                  <dt>{t('retail.campaign.sentBy')}</dt>
                  <dd>
                    {c.approvedByName} · {fmt.dateTime(c.sentAt)}
                  </dd>
                </>
              )}
              <dt>{t('retail.campaign.createdBy')}</dt>
              <dd>{c.createdByName ?? '—'}</dd>
            </dl>
          </section>
          {c.ads.length > 0 && (
            <section className="card card-pad">
              <h2 className="section-title">{t('retail.campaign.connectTitle')}</h2>
              <p className="faint" style={{ margin: 0 }}>
                {t('retail.campaign.connectAds')}
              </p>
            </section>
          )}
        </aside>
      </div>

      {confirm && (
        <SendConfirm
          campaign={c}
          busy={send.isPending}
          error={send.error}
          onClose={() => setConfirm(false)}
          onSend={() => send.mutate()}
        />
      )}
      {metrics && <MetricsDialog campaign={c} onClose={() => setMetrics(false)} onSaved={refresh} />}
      {send.data && (
        <div className="notice success" role="status" data-testid="sent-notice" style={{ marginTop: '1rem' }}>
          <Megaphone size={16} />
          {t('retail.campaign.sentResult', { sent: send.data.sent, skipped: send.data.skipped })}
        </div>
      )}
    </>
  );
}

function SendConfirm({
  campaign,
  busy,
  error,
  onClose,
  onSend,
}: {
  campaign: Campaign;
  busy: boolean;
  error: unknown;
  onClose: () => void;
  onSend: () => void;
}) {
  const { t } = useTranslation();
  const fmt = useFormat();
  const seg = useQuery({
    queryKey: ['segments'],
    queryFn: () => api<{ items: { id: string; reach: Record<string, number> }[] }>('/v1/segments'),
  });
  const reach = seg.data?.items.find((s) => s.id === campaign.segmentId)?.reach;
  return (
    <Dialog
      open
      title={t('retail.campaign.confirmTitle')}
      onClose={onClose}
      onSubmit={onSend}
      testId="confirm-send"
      footer={<DialogButtons onClose={onClose} busy={busy} label={t('retail.campaign.sendNow')} />}
    >
      {reach ? (
        <p style={{ marginTop: 0 }}>
          {t('retail.campaign.confirmMulti', {
            total: fmt.number(reach.total ?? 0),
            parts: campaign.channels
              .map((c) => `${t(`channels.${c}`)} ${fmt.number(reach[c] ?? 0)}`)
              .join(' · '),
          })}
        </p>
      ) : (
        <Loading />
      )}
      <p className="faint">{t('retail.campaign.confirmConsent')}</p>
      {!!error && <ErrorState error={error} />}
    </Dialog>
  );
}

function MetricsDialog({
  campaign,
  onClose,
  onSaved,
}: {
  campaign: Campaign;
  onClose: () => void;
  onSaved: () => void;
}) {
  const { t } = useTranslation();
  const [f, setF] = useState({
    platform: campaign.ads[0]?.platform ?? 'meta',
    day: new Date().toISOString().slice(0, 10),
    spend: '',
    impressions: '',
    clicks: '',
    leads: '',
  });
  const m = useMutation({
    mutationFn: () =>
      api(`/v1/campaigns/${campaign.id}/metrics`, {
        method: 'PUT',
        body: JSON.stringify({
          platform: f.platform,
          days: [
            {
              day: f.day,
              spend: Number(f.spend || 0),
              impressions: Number(f.impressions || 0),
              clicks: Number(f.clicks || 0),
              leads: Number(f.leads || 0),
            },
          ],
        }),
      }),
    onSuccess: () => {
      onSaved();
      onClose();
    },
  });
  const num = (k: 'spend' | 'impressions' | 'clicks' | 'leads', label: string) => (
    <Field id={`mt-${k}`} label={label}>
      <input
        id={`mt-${k}`}
        className="input"
        type="number"
        min={0}
        step="any"
        inputMode="decimal"
        value={f[k]}
        onChange={(e) => setF({ ...f, [k]: e.target.value })}
      />
    </Field>
  );
  return (
    <Dialog
      open
      title={t('retail.campaign.addResults')}
      onClose={onClose}
      onSubmit={() => m.mutate()}
      testId="metrics-dialog"
      footer={<DialogButtons onClose={onClose} busy={m.isPending} label={t('common.save')} />}
    >
      <p className="faint" style={{ marginTop: 0 }}>
        {t('retail.campaign.addResultsHint')}
      </p>
      <div className="form-grid">
        <Field id="mt-platform" label={t('retail.campaign.platform')}>
          <Select
            id="mt-platform"
            value={f.platform}
            onChange={(v) => setF({ ...f, platform: v })}
            options={campaign.ads.map((a) => ({
              value: a.platform,
              label: t(`retail.platformShort.${a.platform}`),
            }))}
          />
        </Field>
        <Field id="mt-day" label={t('retail.campaign.day')}>
          <DateField id="mt-day" value={f.day} onChange={(v) => setF({ ...f, day: v })} />
        </Field>
        {num('spend', t('retail.result.spend'))}
        {num('impressions', t('retail.result.impressions'))}
        {num('clicks', t('retail.campaign.clicks'))}
        {num('leads', t('retail.campaign.leads'))}
      </div>
      {m.isError && <ErrorState error={m.error} />}
    </Dialog>
  );
}
